
import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';
import { Button } from '../../../../components/base/Button';
import { useReputationRewards } from '../../../../hooks/useReputationRewards';

interface CommentFormProps {
  postId: string;
  parentId?: string;
  onCommentAdded: () => void;
  placeholder?: string;
  postAuthorId?: string;
}

export default function CommentForm({
  postId,
  parentId,
  onCommentAdded,
  placeholder = 'Share your thoughts...',
  postAuthorId,
}: CommentFormProps) {
  const { user } = useAuth();
  const { awardPoints } = useReputationRewards();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPointsEarned, setShowPointsEarned] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !content.trim()) return;

    setLoading(true);
    setError('');

    try {
      const { error: commentError } = await supabase
        .from('comments')
        .insert({
          content: content.trim(),
          author_id: user.id,
          post_id: postId,
          parent_id: parentId || null,
        });

      if (commentError) throw commentError;

      // Update post comment count (non‑blocking)
      supabase
        .rpc('increment_post_comment_count', { post_id: postId })
        .catch(() => {
          /* silently ignore */
        });

      // Award reputation points to commenter
      const result = await awardPoints(user.id, 'create_comment');
      if (result.success) {
        setPointsEarned(result.pointsAwarded);
        setShowPointsEarned(true);
        setTimeout(() => setShowPointsEarned(false), 3000);
      }

      // Award points to post author for receiving a comment (if different user)
      if (postAuthorId && postAuthorId !== user.id) {
        awardPoints(postAuthorId, 'receive_comment').catch(() => {
          /* silently ignore */
        });
      }

      setContent('');
      onCommentAdded();
    } catch (err: any) {
      setError(err.message ?? 'Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <form onSubmit={handleSubmit} className="mb-8 relative">
      {/* Points earned notification */}
      {showPointsEarned && (
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-bounce z-10">
          <i className="ri-star-fill mr-1"></i>+{pointsEarned} reputation points!
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex items-start space-x-4">
        <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white font-medium">
            {user.email?.charAt(0).toUpperCase()}
          </span>
        </div>

        <div className="flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={placeholder}
            rows={3}
            maxLength={1000}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-vertical"
            required
          />
          <div className="flex justify-between items-center mt-3">
            <p className="text-sm text-gray-500">{content.length}/1000 characters</p>
            <Button
              type="submit"
              loading={loading}
              disabled={!content.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap"
            >
              {parentId ? 'Reply' : 'Comment'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
